import React, { useEffect, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// Grid-based hour/minute time picker (ported from the tm2-admin portal), used
// in place of the native <input type="time">. `value`/`onChange` speak "HH:mm".
const HOURS = Array.from({ length: 24 }, (_, h) => h)
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5)
const pad = (n) => String(n).padStart(2, '0')

const parseTime = (value) => {
  if (!value) return { hour: null, minute: null }
  const [h, m] = value.split(':').map(Number)
  return {
    hour: Number.isFinite(h) ? h : null,
    minute: Number.isFinite(m) ? m : null,
  }
}

const TimePicker = ({ value, onChange, popupPosition }) => {
  const [{ hour, minute }, setTime] = useState(() => parseTime(value))
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setTime(parseTime(value))
  }, [value])

  const emit = (h, m) => {
    if (h !== null && m !== null && onChange) {
      onChange(`${pad(h)}:${pad(m)}`)
    }
  }

  const onSelectHour = (h) => {
    setTime((prev) => ({ ...prev, hour: h }))
    emit(h, minute)
  }

  const onSelectMinute = (m) => {
    setTime((prev) => ({ ...prev, minute: m }))
    emit(hour, m)
    if (hour !== null) setOpen(false)
  }

  const hasValue = hour !== null && minute !== null
  const display = hasValue
    ? `${pad(hour)}:${pad(minute)}`
    : hour !== null
      ? `${pad(hour)}:MM`
      : minute !== null
        ? `HH:${pad(minute)}`
        : 'HH:MM'

  const [vertical = 'bottom', horizontal = 'left'] = (popupPosition || 'bottom-left').split('-')
  const side = vertical === 'top' ? 'top' : 'bottom'
  const align = horizontal === 'right' ? 'end' : 'start'

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            'tabular-nums',
            !hasValue && 'border-dashed text-muted-foreground'
          )}
        >
          {display}
          <ChevronDown data-icon="inline-end" className="text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side={side} align={align} className="w-auto p-3">
        <div className="mb-1 text-xs font-medium text-muted-foreground">Hour</div>
        <div className="grid grid-cols-6 gap-1.5">
          {HOURS.map((h) => (
            <button
              key={h}
              type="button"
              onClick={() => onSelectHour(h)}
              className={cn(
                'flex size-9 items-center justify-center rounded-md text-sm font-medium tabular-nums transition-colors',
                hour === h
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/60 text-foreground hover:bg-muted'
              )}
            >
              {pad(h)}
            </button>
          ))}
        </div>
        <div className="mt-3 mb-1 text-xs font-medium text-muted-foreground">Minute</div>
        <div className="grid grid-cols-6 gap-1.5">
          {MINUTES.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => onSelectMinute(m)}
              className={cn(
                'flex size-9 items-center justify-center rounded-md text-sm font-medium tabular-nums transition-colors',
                minute === m
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/60 text-foreground hover:bg-muted'
              )}
            >
              {pad(m)}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default TimePicker
